import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  LinearProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompleteIcon,
  PlayArrow as ActiveIcon,
  Assessment as AssessmentIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StatsContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
}));

const StatCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
    transform: 'translateY(-2px)',
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '2rem',
  lineHeight: 1,
  marginBottom: theme.spacing(0.5),
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

const ProgressSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const CampaignStats = ({ campaigns = [], title = "Campaign Overview" }) => {
  // Calculate statistics
  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'active').length,
    ready: campaigns.filter(c => c.status === 'ready').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
    failed: campaigns.filter(c => c.status === 'failed').length,
    totalPosts: campaigns.reduce((sum, c) => sum + (c.totalPosts || 0), 0),
    scheduledPosts: campaigns.reduce((sum, c) => sum + (c.scheduledPosts || 0), 0),
    publishedPosts: campaigns.reduce((sum, c) => sum + (c.publishedPosts || 0), 0),
  };

  // Calculate engagement rates and other metrics
  const totalEngagements = campaigns.reduce((sum, c) => sum + (c.totalEngagements || 0), 0);
  const averageEngagementRate = campaigns.length > 0 
    ? campaigns.reduce((sum, c) => sum + (c.engagementRate || 0), 0) / campaigns.length 
    : 0;

  const completionRate = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;
  const activeRate = stats.total > 0 ? (stats.active / stats.total) * 100 : 0;
  const publishingProgress = stats.totalPosts > 0 ? (stats.publishedPosts / stats.totalPosts) * 100 : 0;

  const mostUsedGoal = campaigns.length > 0 
    ? campaigns.reduce((acc, campaign) => {
        acc[campaign.campaignGoal] = (acc[campaign.campaignGoal] || 0) + 1;
        return acc;
      }, {})
    : {};

  const topGoal = Object.keys(mostUsedGoal).reduce((a, b) => 
    mostUsedGoal[a] > mostUsedGoal[b] ? a : b, 'N/A'
  );

  return (
    <StatsContainer elevation={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AssessmentIcon color="primary" />
        <Typography variant="h6" component="h3">
          {title}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Campaign Count Stats */}
        <Grid item xs={6} sm={3}>
          <StatCard>
            <CampaignIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <StatValue color="primary">
              {stats.total}
            </StatValue>
            <StatLabel>Total Campaigns</StatLabel>
          </StatCard>
        </Grid>

        <Grid item xs={6} sm={3}>
          <StatCard>
            <ActiveIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
            <StatValue color="success.main">
              {stats.active}
            </StatValue>
            <StatLabel>Active</StatLabel>
          </StatCard>
        </Grid>

        <Grid item xs={6} sm={3}>
          <StatCard>
            <ScheduleIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
            <StatValue color="warning.main">
              {stats.ready}
            </StatValue>
            <StatLabel>Ready</StatLabel>
          </StatCard>
        </Grid>

        <Grid item xs={6} sm={3}>
          <StatCard>
            <CompleteIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
            <StatValue color="info.main">
              {stats.completed}
            </StatValue>
            <StatLabel>Completed</StatLabel>
          </StatCard>
        </Grid>
      </Grid>

      {/* Post Stats */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={4}>
          <StatCard>
            <StatValue color="primary">
              {stats.totalPosts}
            </StatValue>
            <StatLabel>Total Posts</StatLabel>
          </StatCard>
        </Grid>

        <Grid item xs={4}>
          <StatCard>
            <StatValue color="warning.main">
              {stats.scheduledPosts}
            </StatValue>
            <StatLabel>Scheduled</StatLabel>
          </StatCard>
        </Grid>

        <Grid item xs={4}>
          <StatCard>
            <StatValue color="success.main">
              {stats.publishedPosts}
            </StatValue>
            <StatLabel>Published</StatLabel>
          </StatCard>
        </Grid>
      </Grid>

      {/* Progress and Insights */}
      <ProgressSection>
        <Typography variant="subtitle1" gutterBottom>
          Progress & Insights
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Publishing Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(publishingProgress)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={publishingProgress}
            color="success"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Campaign Completion Rate
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(completionRate)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={completionRate}
            color="info"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Key Metrics */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {averageEngagementRate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg. Engagement Rate
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {totalEngagements.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Engagements
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Top Campaign Goal */}
        {topGoal !== 'N/A' && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Most Popular Campaign Goal
            </Typography>
            <Chip 
              label={topGoal.replace('_', ' ').toUpperCase()} 
              color="primary" 
              variant="outlined"
            />
          </Box>
        )}
      </ProgressSection>
    </StatsContainer>
  );
};

export default CampaignStats; 